import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Upload, X, Loader2, Send, MapPin, CheckCircle2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CONTACT, SERVICE_TYPES } from "@/data/site";
import { compressImage } from "@/lib/compressImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", phone: "", email: "", commune: "", service_type: "", description: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const raw of files) {
        const file = await compressImage(raw);
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/upload`, fd);
        setAttachments((a) => [...a, data]);
      }
      toast.success("Photo(s) ajoutée(s)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.service_type || !form.description) {
      toast.error("Merci de remplir les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, { ...form, attachments });
      setDone(true);
      setForm(empty);
      setAttachments([]);
      toast.success("Votre demande a bien été envoyée !");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-line bg-white px-4 py-3 text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-olive focus:ring-offset-2 focus:ring-offset-sand transition-all";

  return (
    <div data-testid="contact-page">
      <Seo title="Demandez votre devis — Sandro Services 06 | Alpes-Maritimes (06)" description="Contactez Sandro Services 06 pour un devis : entretien de jardins, nettoyage extérieur et petites réparations dans les Alpes-Maritimes." />
      <PageHero chapter="Contact" title="Demandez votre devis" subtitle="Décrivez votre projet et recevez une réponse adaptée. Vous pouvez joindre des photos pour faciliter l'estimation." />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-16 grid lg:grid-cols-[1fr_0.8fr] gap-12 items-start">
        {/* FORM */}
        <Reveal>
          {done ? (
            <div data-testid="quote-success" className="bg-white border border-line rounded-2xl p-10 text-center">
              <CheckCircle2 size={48} className="text-olive mx-auto" />
              <h2 className="mt-6 font-display text-3xl text-forest">Merci !</h2>
              <p className="mt-3 text-ink-soft">Votre demande a bien été envoyée. Nous reviendrons vers vous rapidement.</p>
              <button onClick={() => setDone(false)} className="mt-8 inline-flex items-center gap-2 bg-olive text-white rounded-full px-7 py-3 font-semibold hover:bg-olive-hover transition-all">
                Envoyer une autre demande
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="bg-white border border-line rounded-2xl p-6 md:p-10 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-forest">Nom / prénom *</label>
                  <input data-testid="quote-name" value={form.name} onChange={set("name")} className={inputCls + " mt-2"} placeholder="Votre nom" />
                </div>
                <div>
                  <label className="text-sm font-medium text-forest">Téléphone *</label>
                  <input data-testid="quote-phone" value={form.phone} onChange={set("phone")} className={inputCls + " mt-2"} placeholder="Votre téléphone" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-forest">Adresse e-mail *</label>
                  <input data-testid="quote-email" type="email" value={form.email} onChange={set("email")} className={inputCls + " mt-2"} placeholder="vous@email.fr" />
                </div>
                <div>
                  <label className="text-sm font-medium text-forest">Commune</label>
                  <input data-testid="quote-commune" value={form.commune} onChange={set("commune")} className={inputCls + " mt-2"} placeholder="Ex : Nice, Cannes…" />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-forest">Type de prestation *</label>
                <select data-testid="quote-service" value={form.service_type} onChange={set("service_type")} className={inputCls + " mt-2"}>
                  <option value="">Sélectionnez une prestation</option>
                  {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-forest">Description du projet *</label>
                <textarea data-testid="quote-description" value={form.description} onChange={set("description")} rows={5} className={inputCls + " mt-2 resize-none"} placeholder="Décrivez votre besoin…" />
              </div>

              {/* Upload */}
              <div>
                <label className="text-sm font-medium text-forest">Photos (facultatif)</label>
                <div onClick={() => fileRef.current?.click()} data-testid="quote-upload-zone" className="mt-2 cursor-pointer border-2 border-dashed border-line rounded-xl px-4 py-6 text-center hover:border-olive transition-colors">
                  {uploading ? (
                    <span className="inline-flex items-center gap-2 text-ink-soft"><Loader2 className="animate-spin" size={18} /> Envoi en cours…</span>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-ink-soft"><Upload size={18} /> Ajouter des photos de votre projet</span>
                  )}
                  <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={handleFiles} data-testid="quote-file-input" />
                </div>
                {attachments.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="attachment-list">
                    {attachments.map((a) => (
                      <div key={a.id} className="relative group rounded-xl overflow-hidden border border-line aspect-square">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} alt={a.filename} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removeAttachment(a.id)} className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-forest/80 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button type="submit" data-testid="quote-submit-button" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-olive text-white rounded-full px-8 py-4 font-semibold hover:bg-olive-hover transition-all disabled:opacity-60">
                {submitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                Envoyer ma demande
              </button>
            </form>
          )}
        </Reveal>

        {/* QUICK CONTACT */}
        <Reveal delay={0.1} className="lg:sticky lg:top-28">
          <div className="bg-forest text-sand rounded-2xl p-8 grain">
            <h2 className="font-display text-3xl">Contact rapide</h2>
            <p className="mt-3 text-sand/75 text-sm">Une question ? Contactez-nous directement.</p>
            <div className="mt-8 space-y-4">
              <a href={`tel:${CONTACT.phone}`} data-testid="contact-call-btn" className="flex items-center gap-4 bg-sand/10 hover:bg-sand/20 rounded-xl px-5 py-4 transition-colors">
                <span className="w-11 h-11 rounded-full bg-olive grid place-items-center text-white"><Phone size={18} /></span>
                <span><span className="block text-xs text-sand/60">Appeler</span><span className="font-semibold">{CONTACT.phoneDisplay}</span></span>
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-btn" className="flex items-center gap-4 bg-sand/10 hover:bg-sand/20 rounded-xl px-5 py-4 transition-colors">
                <span className="w-11 h-11 rounded-full bg-olive grid place-items-center text-white"><Mail size={18} /></span>
                <span><span className="block text-xs text-sand/60">E-mail</span><span className="font-semibold break-all">{CONTACT.email}</span></span>
              </a>
              <div className="flex items-center gap-4 px-5 py-4">
                <span className="w-11 h-11 rounded-full bg-sand/15 grid place-items-center text-olive"><MapPin size={18} /></span>
                <span><span className="block text-xs text-sand/60">Zone</span><span className="font-semibold">{CONTACT.zone}</span></span>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
